import * as React from 'react';
import Card, {
  PaddingOptions
} from '../../../sharedComponents/core/Cards/Card';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import StatusSection from './StatusSection';
import { useRouter } from 'found';
import { StatusCard_company } from './__generated__/StatusCard_company.graphql';
import {
  HAS_ACTIVE_BACKGROUND_CHECK,
  HAS_ACTIVE_DOCUMENT,
  HAS_EXPIRED_BACKGROUND_CHECK,
  HAS_EXPIRED_DOCUMENT
} from 'views/DelegatesPage';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    background: 'white'
  },
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  heading: {
    fontSize: theme.fontSizes.small,
    color: theme.colors.primaryBlack,
    padding: '14px 20px'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  button: {
    alignSelf: 'center',
    marginTop: '50px'
  },
  marginBottom: {
    margin: '0 0 15px 0'
  },
  card: {
    padding: '22px 24px',
    borderBottom: '1px solid #F6F6F7',
    borderRight: '1px solid #F6F6F7',
    fontSize: 14
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr'
  },
  topTitle: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  catTitle: {
    fontWeight: 800
  },
  catLabel: {
    color: '#6A728F',
    fontSize: 13
  },
  graphContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    marginTop: 13
  },
  graph: {
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
    margin: '10px 0 10px 0'
  },
  certPercentage: {
    fontSize: 14,
    fontWeight: 800
  },
  percentageLabel: {
    color: '#6A728F',
    fontSize: 12,
    textAlign: 'center'
  },
  infoText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  divider: {
    width: '100%',
    height: 1,
    background: '#F6F6F7'
  },
  empty: {
    padding: 20,
    color: '#6a728f',
    fontSize: 14
  }
}));

type Props = {
  company: StatusCard_company;
  onClick?: Function;
  padding?: PaddingOptions;
  className?: string;
};

function StatusCard({ company, onClick, padding = 'none', className }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { router } = useRouter();

  const certificatesStatus = company.certificatesStatus;
  const simulatorCompliance = company.simulatorCompliance;
  const backgroundStatus = company.backgroundCheckStatus;
  let numCards = certificatesStatus.secondaryCategoryStatuses?.length;

  if (simulatorCompliance.activeUsers > 0) {
    numCards += 1;
  }

  return (
    <Card
      className={classNames(classes.container, className)}
      padding={padding}
    >
      <div className={classes.root}>
        <div className={classes.cards}>
          {certificatesStatus?.secondaryCategoryStatuses?.map(
            (status, index) => (
              <StatusSection
                title={status.secondaryCategory?.name ?? ''}
                subLabel={`${
                  status.totals.available + status.totals.expired
                } certificates`}
                inner={{
                  labelValue: `${
                    status.totals.expired + status.totals.incomplete
                  }`,
                  label: 'Incomplete / expired courses',
                  value: status.totals.expired + status.totals.incomplete,
                  onClick: () => {
                    router.push(
                      `/app/delegates?secondaryCategory=${
                        status?.secondaryCategory?.uuid ??
                        '00000000-0000-0000-0000-000000000000'
                      }&hasExpiredCert=true`
                    );
                  }
                }}
                outer={{
                  labelValue: `${status.totals.available}`,
                  label: 'Active certificates',
                  value: status.totals.available,
                  onClick: () => {
                    router.push(
                      `/app/delegates?secondaryCategory=${
                        status?.secondaryCategory?.uuid ??
                        '00000000-0000-0000-0000-000000000000'
                      }&hasActiveCert=true`
                    );
                  }
                }}
                normal={{
                  labelValue: `${Math.round(
                    (status.totals.available /
                      (status.totals.available +
                        status.totals.expired +
                        status.totals.incomplete)) *
                      100
                  )}
                  %`,
                  label: 'Complete courses'
                }}
                rightBorder={index % 2 === 0}
                bottomBorder={
                  numCards % 2 ? index <= numCards - 2 : index <= numCards - 1
                }
              />
            )
          )}
          {simulatorCompliance.activeUsers > 0 && (
            <StatusSection
              title={'6x6 Training'}
              subLabel={`${simulatorCompliance.activeUsers} users`}
              inner={{
                labelValue: `${simulatorCompliance.uncompliantUsers}`,
                label: 'Non-compliant users',
                value: simulatorCompliance.uncompliantUsers,
                onClick: () => {
                  router.push(`/app/delegates?simulatorCompliant=false`);
                }
              }}
              outer={{
                labelValue: `${simulatorCompliance.compliantUsers}`,
                label: 'Compliant users',
                value: simulatorCompliance.compliantUsers,
                onClick: () => {
                  router.push(`/app/delegates?simulatorCompliant=true`);
                }
              }}
              normal={{
                labelValue: `${simulatorCompliance.activeUsers}`,
                label: 'Active users'
              }}
            />
          )}
          {(backgroundStatus.available > 0 ||
            backgroundStatus.expired > 0 ||
            backgroundStatus.expiringSoon > 0 ||
            backgroundStatus.incomplete > 0) && (
            <StatusSection
              title={'Background Checks'}
              subLabel={`${
                backgroundStatus.available + backgroundStatus.expired
              } background checks`}
              inner={{
                labelValue: `${
                  backgroundStatus.expired + backgroundStatus.incomplete
                }`,
                label: 'Expired / invalid background checks',
                value: backgroundStatus.expired + backgroundStatus.incomplete,
                onClick: () => {
                  router.push(
                    `/app/delegates?filter=${HAS_EXPIRED_BACKGROUND_CHECK}`
                  );
                }
              }}
              outer={{
                labelValue: `${backgroundStatus.available}`,
                label: 'Valid background checks',
                value: backgroundStatus.available,
                onClick: () => {
                  router.push(
                    `/app/delegates?filter=${HAS_ACTIVE_BACKGROUND_CHECK}`
                  );
                }
              }}
              normal={{
                labelValue: `${backgroundStatus.available}`,
                label: 'Valid checks'
              }}
            />
          )}
          {/* <StatusSection
            title="Documents"
            subLabel={`${
              (company.documentStatus?.available ?? 0) +
              (company.documentStatus?.expired ?? 0)
            } documents`}
            inner={{
              labelValue: `${company.documentStatus?.expired}`,
              label: 'Expired documents',
              value: company.documentStatus?.expired ?? 0,
              onClick: () => {
                router.push(`/app/delegates?filter=${HAS_EXPIRED_DOCUMENT}`);
              }
            }}
            outer={{
              labelValue: `${company.documentStatus?.available}`,
              label: 'Active documents',
              value: company.documentStatus?.available ?? 0,
              onClick: () => {
                router.push(`/app/delegates?filter=${HAS_ACTIVE_DOCUMENT}`);
              }
            }}
            normal={{
              labelValue: `${company.documentStatus?.available}`,
              label: 'Active documents'
            }}
          /> */}
          {numCards === 0 && (
            <div className={classes.empty}>No delegates trained yet</div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default createFragmentContainer(StatusCard, {
  company: graphql`
    fragment StatusCard_company on Company {
      certificatesStatus {
        secondaryCategoryStatuses {
          secondaryCategory {
            name
            uuid
          }
          totals {
            available
            expired
            expiringSoon
            incomplete
          }
        }
      }
      simulatorCompliance {
        activeUsers
        compliantUsers
        uncompliantUsers
      }
      documentStatus {
        available
        expired
        expiringSoon
        incomplete
      }
      backgroundCheckStatus {
        available
        expired
        expiringSoon
        incomplete
      }
    }
  `
});
